// Single source of truth for which product modules are enabled in Admin CMS + their order.
// Used by Navbar, HomePage and ResultsPageLayout so they all stay consistent.

import { useEffect, useState } from 'react';
import { api } from './api';

const STORAGE_KEY = 'elite_modules_config';
const DEFAULT_ORDER = ['hotels', 'flights', 'trains', 'buses', 'cabs', 'villas', 'packages', 'tours', 'visa', 'cruise', 'forex', 'insurance'];
const FALLBACK = {
  modules: { hotels: true, flights: true, trains: true, buses: true, cabs: true, villas: true },
  order: DEFAULT_ORDER,
};

let cache = null;
let inFlight = null;

const readCached = () => {
  if (cache) return cache;
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (v && typeof v === 'object' && v.modules) return v;
  } catch { /* ignore */ }
  return FALLBACK;
};

const fetchModules = async () => {
  if (inFlight) return inFlight;
  inFlight = api.get('/services/modules-config')
    .then((r) => {
      cache = r.data && r.data.modules ? r.data : FALLBACK;
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cache)); } catch { /* quota */ }
      return cache;
    })
    .catch(() => (cache = FALLBACK))
    .finally(() => { inFlight = null; });
  return inFlight;
};

export const useModules = () => {
  // Back-compat: components that previously expected just the flat bool map still work — we
  // return that map by default, but expose .order via the `useModulesConfig` hook.
  const [modules, setModules] = useState(readCached().modules);
  useEffect(() => {
    let cancelled = false;
    fetchModules().then((m) => { if (!cancelled) setModules(m.modules); });
    return () => { cancelled = true; };
  }, []);
  return modules;
};

export const useModulesConfig = () => {
  const [cfg, setCfg] = useState(readCached());
  useEffect(() => {
    let cancelled = false;
    fetchModules().then((m) => { if (!cancelled) setCfg(m); });
    // Re-fetch when admin saves new order
    const onChanged = () => fetchModules().then((m) => { if (!cancelled) setCfg(m); });
    window.addEventListener('modules-config-changed', onChanged);
    return () => { cancelled = true; window.removeEventListener('modules-config-changed', onChanged); };
  }, []);
  return cfg;
};

// Invalidate cache (called by admin UI after saving).
export const invalidateModulesConfig = () => {
  cache = null;
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent('modules-config-changed'));
};
